import { toArray, getTotal } from "utils/invoiceListResponse";
import RoutePageTitle from "components/RoutePageTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { normalizeRoleLevel } from "utils/roleUtils";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Pagination from "@mui/material/Pagination";
import * as XLSX from "xlsx";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { ColumnVisibilityMenu, useColumnVisibility } from "./ColumnVisibilityMenu";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const RANGE_DOWNLOAD_SIZE = 1000000;
const INVOICE_EXPORT_PAGE_SIZE =
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_CRM_API_BASE === "http://127.0.0.1:18092"
    ? 1000
    : 100;
const READABLE_TEXT_COLOR = "#344767";

const INVOICE_LIST_QUERY = `
  query GetInvoiceList($input: JSON!) {
    GetInvoiceList(input: $input)
  }
`;

const INVOICE_CONFIG_QUERY = `
  query GetInvoiceConfig {
    GetInvoiceConfig
  }
`;

const INVOICE_CONFIG_LIST_QUERY = `
  query GetInvoiceConfigList($input: JSON!) {
    GetInvoiceConfigList(input: $input)
  }
`;

const UPDATE_INVOICE_CONFIG_MUTATION = `
  mutation UpdateInvoiceConfig($input: JSON!) {
    UpdateInvoiceConfig(input: $input)
  }
`;

const CREATE_INVOICE_CONFIG_MUTATION = `
  mutation CreateInvoiceConfig($input: JSON!) {
    CreateInvoiceConfig(input: $input)
  }
`;

const DELETE_INVOICE_CONFIG_MUTATION = `
  mutation DeleteInvoiceConfig($input: JSON!) {
    DeleteInvoiceConfig(input: $input)
  }
`;

const invoiceColumns = [
  { key: "invoiceNo", label: "發票號碼" },
  { key: "createDate", label: "開立時間" },
  { key: "status", label: "狀態" },
  { key: "type", label: "類型" },
  { key: "category", label: "類別" },
  { key: "salesAmount", label: "銷售額" },
  { key: "taxAmount", label: "稅額" },
  { key: "taxRate", label: "稅率" },
  { key: "identifier", label: "統編" },
  { key: "customerEmail", label: "Email" },
  { key: "customerPhone", label: "電話" },
  { key: "carrierType", label: "載具類型" },
  { key: "carrierNum", label: "載具號碼" },
  { key: "loveCode", label: "愛心碼" },
  { key: "voidDate", label: "作廢時間" },
];

const rangeColumns = [
  { key: "id", label: "ID" },
  { key: "invoice_word", label: "字軌" },
  { key: "invoice_start", label: "起號" },
  { key: "invoice_end", label: "迄號" },
  { key: "invoice_current", label: "目前號碼" },
  { key: "year", label: "年" },
  { key: "created_at", label: "建立時間" },
];

const normalizeRangeRow = (row) => ({
  ...row,
  invoice_word: row?.invoice_word ?? row?.prefix ?? "",
  invoice_start: row?.invoice_start ?? row?.startNumber ?? "",
  invoice_end: row?.invoice_end ?? row?.endNumber ?? "",
  invoice_current: row?.invoice_current ?? row?.issued ?? "",
  year: row?.year ?? row?.period ?? "",
  created_at: row?.created_at ?? row?.updated_at ?? row?.createDate ?? "",
});

const normalizeRangeRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row) => normalizeRangeRow(row));

const formatCell = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const invoiceTimeValue = (row) =>
  new Date(
    row?.createDate ||
      row?.iisCreateDate ||
      row?.iiDate ||
      row?.invoiceDate ||
      row?.created_at ||
      row?.createdAt ||
      0
  ).getTime() || 0;

const sortInvoicesDesc = (rows) =>
  [...rows].sort((a, b) => invoiceTimeValue(b) - invoiceTimeValue(a));

const normalizeDate = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const downloadRows = (rows, columns, filename, sheetName) => {
  const headers = columns.map((column) => column.label);
  const body = rows.map((row) => columns.map((column) => formatCell(row[column.key])));
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, filename);
};

function InvoiceDetailPage() {
  const { userData, roleLevel, merchantTags } = useUserContext() || {};
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const showMerchantFilter = normalizeRoleLevel(roleLevel) === 3;

  const [tab, setTab] = useState("issued");
  const [rows, setRows] = useState([]);
  const [rangeRows, setRangeRows] = useState([]);
  const merchantOptions = useMemo(
    () => (Array.isArray(merchantTags) ? merchantTags.filter(Boolean) : []),
    [merchantTags]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState({
    keyword: "",
    identifier: "",
    start: "",
    end: "",
    status: "",
    merchantTags: [],
  });
  const [configOpen, setConfigOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [configRows, setConfigRows] = useState([]);
  const [configForm, setConfigForm] = useState({ love_code: "", donee_name: "" });
  const [rangeForm, setRangeForm] = useState({
    invoice_word: "",
    invoice_start: "",
    invoice_end: "",
    year: "",
    month: "",
  });

  const updateFilters = useCallback((updater) => {
    setPage(1);
    setFilters((prev) => (typeof updater === "function" ? updater(prev) : { ...prev, ...updater }));
  }, []);

  const fetchGraphQL = useCallback(
    async (query, variables = {}) => {
      // Local snapshot preview uses the real CRM invoice handler; normal GraphQL is unchanged.
      if (
        query === INVOICE_LIST_QUERY &&
        process.env.NODE_ENV === "development" &&
        ["127.0.0.1", "localhost"].includes(window.location.hostname) &&
        process.env.REACT_APP_CRM_API_BASE === "http://127.0.0.1:18092"
      ) {
        const response = await fetch(`${process.env.REACT_APP_CRM_API_BASE}/api/v1/invoice/list`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(variables.input),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || "發票查詢失敗");
        return { GetInvoiceList: result };
      }
      const headers = { "Content-Type": "application/json", Accept: "application/json" };
      const response = await fetch(backendUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ query, variables }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.errors?.length) {
        throw new Error(
          json?.errors?.[0]?.message || `GraphQL request failed (${response.status})`
        );
      }
      return json?.data || {};
    },
    [backendUrl]
  );

  const buildListInput = useCallback(
    (override = {}) => {
      const payload = {
        invoiceNo: filters.keyword.trim(),
        identifier: filters.identifier.trim(),
        start: normalizeDate(filters.start),
        end: normalizeDate(filters.end),
        status: override.status !== undefined ? override.status : filters.status,
        page: override.page || page,
        size: override.size || pageSize,
        email,
      };
      if (filters.merchantTags.length) {
        payload.merchantCode = filters.merchantTags;
      }
      return payload;
    },
    [email, filters, page, pageSize]
  );

  const fetchInvoices = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchGraphQL(INVOICE_LIST_QUERY, { input: buildListInput() });
      const result = data?.GetInvoiceList || {};
      const nextRows = sortInvoicesDesc(toArray(result));
      setRows(nextRows);
      setTotal(getTotal(result, nextRows.length));
    } catch (err) {
      setError(err.message || "查詢發票失敗");
    } finally {
      setLoading(false);
    }
  }, [buildListInput, email, fetchGraphQL, tab]);

  const handleSearchInvoices = useCallback(() => {
    setPage(1);
    if (page === 1) {
      fetchInvoices();
    }
  }, [fetchInvoices, page]);

  const fetchRanges = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchGraphQL(INVOICE_CONFIG_LIST_QUERY, {
        input: { email, page: 1, size: 1000 },
      });
      setRangeRows(normalizeRangeRows(toArray(data?.GetInvoiceConfigList || {})));
    } catch (err) {
      setError(err.message || "查詢發票字軌失敗");
    } finally {
      setLoading(false);
    }
  }, [email, fetchGraphQL]);

  useEffect(() => {
    if (tab === "ranges") {
      fetchRanges();
    } else {
      fetchInvoices();
    }
  }, [fetchInvoices, fetchRanges, tab]);

  const handleOpenConfig = async () => {
    setConfigOpen(true);
    setError("");
    try {
      const data = await fetchGraphQL(INVOICE_CONFIG_QUERY);
      const config = data?.GetInvoiceConfig || {};
      const configList = toArray(config);
      setConfigRows(configList.length ? configList : [config?.data || config].filter(Boolean));
    } catch (err) {
      setError(err.message || "查詢愛心碼設定失敗");
    }
  };

  const handleSaveConfig = async () => {
    try {
      await fetchGraphQL(UPDATE_INVOICE_CONFIG_MUTATION, {
        input: {
          loveCode: configForm.love_code.trim(),
          doneeName: configForm.donee_name.trim(),
        },
      });
      setConfigForm({ love_code: "", donee_name: "" });
      await handleOpenConfig();
    } catch (err) {
      setError(err.message || "更新愛心碼失敗");
    }
  };

  const handleCreateRange = async () => {
    try {
      const prefix = String(rangeForm.invoice_word || "").trim();
      const start = Number(rangeForm.invoice_start);
      const end = Number(rangeForm.invoice_end);
      const year = Number(rangeForm.year);
      const month = Number(rangeForm.month);

      if (
        !email ||
        !prefix ||
        !Number.isInteger(start) ||
        !Number.isInteger(end) ||
        !Number.isInteger(year) ||
        !Number.isInteger(month) ||
        month < 1 ||
        month > 12
      ) {
        throw new Error("字軌、起訖號與年月格式不正確");
      }

      const result = await fetchGraphQL(CREATE_INVOICE_CONFIG_MUTATION, {
        input: {
          email,
          year,
          month,
          prefix,
          range: [{ start, end }],
        },
      });
      const createResult = result?.CreateInvoiceConfig || {};
      const businessMessage = createResult?.data?.message;
      if (businessMessage && businessMessage !== "success") {
        throw new Error(businessMessage);
      }
      setRangeOpen(false);
      setRangeForm({ invoice_word: "", invoice_start: "", invoice_end: "", year: "", month: "" });
      await fetchRanges();
    } catch (err) {
      setError(err.message || "建立發票字軌失敗");
    }
  };

  const handleDeleteRange = async (id) => {
    if (!id) return;
    try {
      await fetchGraphQL(DELETE_INVOICE_CONFIG_MUTATION, {
        input: { email, id: String(id) },
      });
      await fetchRanges();
    } catch (err) {
      setError(err.message || "刪除發票字軌失敗");
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    try {
      if (tab === "ranges") {
        const data = await fetchGraphQL(INVOICE_CONFIG_LIST_QUERY, {
          input: { email, page: 1, size: RANGE_DOWNLOAD_SIZE },
        });
        downloadRows(
          normalizeRangeRows(toArray(data?.GetInvoiceConfigList || {})),
          rangeColumns,
          "invoice-ranges.xlsx",
          "InvoiceRanges"
        );
        return;
      }

      const firstPageData = await fetchGraphQL(INVOICE_LIST_QUERY, {
        input: buildListInput({
          page: 1,
          size: INVOICE_EXPORT_PAGE_SIZE,
        }),
      });
      const firstPageResult = firstPageData?.GetInvoiceList || {};
      const rows = toArray(firstPageResult);
      const total = getTotal(firstPageResult, rows.length);
      const totalPages = Math.max(1, Math.ceil(total / INVOICE_EXPORT_PAGE_SIZE));

      for (let currentPage = 2; currentPage <= totalPages; currentPage += 1) {
        const pageData = await fetchGraphQL(INVOICE_LIST_QUERY, {
          input: buildListInput({ page: currentPage, size: INVOICE_EXPORT_PAGE_SIZE }),
        });
        rows.push(...toArray(pageData?.GetInvoiceList || {}));
      }

      downloadRows(sortInvoicesDesc(rows), invoiceColumns, "invoice-detail.xlsx", "Invoices");
    } catch (err) {
      setError(err.message || "下載失敗");
    } finally {
      setDownloading(false);
    }
  };

  const displayedColumns = tab === "ranges" ? rangeColumns : invoiceColumns;
  const { columnHeadersSetting, handleChangeColumnHeaderVisible, visibleColumns } =
    useColumnVisibility(displayedColumns, tab);
  const displayedRows = tab === "ranges" ? rangeRows : rows;
  const displayedTotal = tab === "ranges" ? displayedRows.length : total || displayedRows.length;
  const totalPages = Math.max(1, Math.ceil(displayedTotal / pageSize));

  useEffect(() => {
    if (tab !== "ranges" && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, tab, totalPages]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDTypography variant="h6" color="white">
                  <RoutePageTitle />
                </MDTypography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="outlined" color="inherit" onClick={handleOpenConfig}>
                    愛心碼設定
                  </Button>
                  <Button variant="outlined" color="inherit" onClick={() => setRangeOpen(true)}>
                    建立字軌
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleDownload}
                    disabled={downloading}
                  >
                    {downloading ? "下載中..." : "下載"}
                  </Button>
                </Stack>
              </MDBox>
              <MDBox p={3}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                <Tabs
                  value={tab}
                  onChange={(_, value) => {
                    setTab(value);
                    setPage(1);
                  }}
                  sx={{ mb: 3 }}
                >
                  <Tab label="已開立發票" value="issued" />
                  <Tab label="發票字軌" value="ranges" />
                </Tabs>
                {tab !== "ranges" && (
                  <Grid container spacing={2} alignItems="center" mb={3}>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="發票號碼"
                        value={filters.keyword}
                        onChange={(e) =>
                          updateFilters((prev) => ({ ...prev, keyword: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="統編"
                        value={filters.identifier}
                        onChange={(e) =>
                          updateFilters((prev) => ({ ...prev, identifier: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        type="date"
                        label="開始日期"
                        InputLabelProps={{ shrink: true }}
                        value={filters.start}
                        onChange={(e) =>
                          updateFilters((prev) => ({ ...prev, start: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        type="date"
                        label="結束日期"
                        InputLabelProps={{ shrink: true }}
                        value={filters.end}
                        onChange={(e) =>
                          updateFilters((prev) => ({ ...prev, end: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        select
                        fullWidth
                        label="狀態"
                        value={filters.status}
                        onChange={(e) =>
                          updateFilters((prev) => ({ ...prev, status: e.target.value }))
                        }
                      >
                        <MenuItem value="">全部</MenuItem>
                        <MenuItem value="issued">已開立</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button fullWidth variant="contained" onClick={handleSearchInvoices}>
                        查詢
                      </Button>
                    </Grid>
                    {showMerchantFilter && (
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                              <Stack direction="row" gap={1} flexWrap="wrap">
                                {selected.map((value) => (
                                  <Chip key={value} label={value} size="small" />
                                ))}
                              </Stack>
                            ),
                          }}
                          label="商戶標籤"
                          value={filters.merchantTags}
                          onChange={(e) =>
                            updateFilters((prev) => ({ ...prev, merchantTags: e.target.value }))
                          }
                        >
                          {merchantOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Grid>
                )}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  mb={2}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <TextField
                    select
                    size="small"
                    label="每頁筆數"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    sx={{ width: 140 }}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </TextField>
                  <MDTypography variant="button" sx={{ color: READABLE_TEXT_COLOR }}>
                    {tab === "ranges"
                      ? `共 ${displayedTotal} 筆`
                      : `共 ${displayedTotal} 筆，第 ${page} / ${totalPages} 頁`}
                  </MDTypography>
                  <MDBox ml="auto">
                    <ColumnVisibilityMenu
                      columnHeadersSetting={columnHeadersSetting}
                      onChangeColumnHeaderVisible={handleChangeColumnHeaderVisible}
                    />
                  </MDBox>
                </Stack>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 650 }}>
                    <Table stickyHeader>
                      <TableHead sx={{ display: "table-header-group", whiteSpace: "nowrap" }}>
                        <TableRow>
                          {visibleColumns.map((column) => (
                            <TableCell key={column.key} sx={{ textAlign: "center" }}>
                              {column.label}
                            </TableCell>
                          ))}
                          {tab === "ranges" && (
                            <TableCell sx={{ textAlign: "center" }}>操作</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{ whiteSpace: "nowrap" }}>
                        {displayedRows.map((row, index) => (
                          <TableRow key={row.id || row.invoiceNo || index}>
                            {visibleColumns.map((column) => (
                              <TableCell key={column.key} sx={{ textAlign: "center" }}>
                                {formatCell(row[column.key])}
                              </TableCell>
                            ))}
                            {tab === "ranges" && (
                              <TableCell>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteRange(row.id)}
                                >
                                  刪除
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {tab !== "ranges" && (
                  <MDBox mt={2} display="flex" justifyContent="flex-end">
                    <Pagination
                      count={totalPages}
                      page={page}
                      color="primary"
                      onChange={(_, value) => setPage(value)}
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>發票愛心碼設定</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="愛心碼"
                value={configForm.love_code}
                onChange={(e) => setConfigForm((prev) => ({ ...prev, love_code: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="受捐贈單位"
                value={configForm.donee_name}
                onChange={(e) => setConfigForm((prev) => ({ ...prev, donee_name: e.target.value }))}
              />
            </Grid>
          </Grid>
          <TableContainer component={Paper} sx={{ mt: 3, maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {["love_code", "donee_name", "updated_at"].map((key) => (
                    <TableCell key={key}>{key}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {configRows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatCell(row.love_code || row.loveCode)}</TableCell>
                    <TableCell>{formatCell(row.donee_name || row.doneeName)}</TableCell>
                    <TableCell>{formatCell(row.updated_at || row.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigOpen(false)}>關閉</Button>
          <Button variant="contained" onClick={handleSaveConfig}>
            儲存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rangeOpen} onClose={() => setRangeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>建立發票字軌</DialogTitle>
        <DialogContent>
          {rangeColumns.slice(1, 5).map((column) => (
            <TextField
              key={column.key}
              fullWidth
              margin="normal"
              label={column.label}
              value={rangeForm[column.key] || ""}
              onChange={(e) => setRangeForm((prev) => ({ ...prev, [column.key]: e.target.value }))}
            />
          ))}
          <TextField
            fullWidth
            margin="normal"
            label="年 (YYYY)"
            value={rangeForm.year}
            onChange={(e) => setRangeForm((prev) => ({ ...prev, year: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="月 (1-12)"
            value={rangeForm.month}
            onChange={(e) => setRangeForm((prev) => ({ ...prev, month: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRangeOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleCreateRange}>
            建立
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default InvoiceDetailPage;
